import type { ComponentChildren } from 'preact';
import { FileText, Heart, ScrollText, ShieldCheck, Users } from 'lucide-preact';
import { openModal, replace, route } from '../state/nav';
import { Modal, Screen, Tabs } from '../ui/kit';
import parentsHtml from '../../legal/parents.html?raw';
import privacyHtml from '../../legal/privacy.html?raw';
import termsHtml from '../../legal/terms.html?raw';

export type LegalDocId = 'privacy' | 'terms' | 'parents' | 'credits';

const TITLES: Record<LegalDocId, string> = {
  privacy: 'Privacy Policy',
  terms: 'Terms of Service',
  parents: "Parents' Guide",
  credits: 'Credits',
};

export const SUPPORT_EMAIL = 'support@voxelmarines.com';
// The privacy policy, terms and parents' guide are plain HTML, shared with the website's /privacy, /terms and /parents pages
// (vite.config.ts puts the same files into privacy.html etc.), so the text that store reviewers read is the text players see.
const DOCS: Record<Exclude<LegalDocId, 'credits'>, string> = { privacy: privacyHtml, terms: termsHtml, parents: parentsHtml };

function Section(props: { title: string; children: ComponentChildren }) {
  return (
    <section class="legal-section">
      <h3>{props.title}</h3>
      {props.children}
    </section>
  );
}

function Credits() {
  return (
    <>
      <p class="legal-lead">Voxel Marines is built by a small team that loves blowing up blocks.</p>
      <Section title="Made with">
        <ul>
          <li>Three.js — 3D rendering</li>
          <li>Preact &amp; Preact Signals — interface</li>
          <li>Lucide — icons (ISC license)</li>
          <li>Lilita One &amp; Nunito — fonts (SIL Open Font License)</li>
          <li>Hono &amp; Kysely — game services</li>
          <li>Every sound is synthesized live in your browser — no audio files.</li>
        </ul>
      </Section>
      <Section title="Thanks">
        <p>To every playtester who knocked down a clock tower just to see what happens. You're the best.</p>
      </Section>
    </>
  );
}

export function LegalBody(props: { doc: LegalDocId }) {
  if (props.doc === 'credits')
    return (
      <div class="legal-body">
        <Credits />
      </div>
    );
  // Our own static files, never anything a player wrote.
  return <div class="legal-body" dangerouslySetInnerHTML={{ __html: DOCS[props.doc] }} />;
}

export function openLegal(doc: LegalDocId): void {
  openModal((close) => (
    <Modal title={TITLES[doc]} icon={<ScrollText size={22} />} size="lg" onClose={close} class="legal-modal">
      <LegalBody doc={doc} />
    </Modal>
  ));
}

export function LegalScreen() {
  const doc = (route.value.params.doc as LegalDocId) || 'privacy';
  return (
    <Screen
      title="Legal & Info"
      icon={<FileText size={26} />}
      right={<span />}
      tabs={
        <Tabs<LegalDocId>
          value={doc}
          onChange={(d) => replace('legal', { doc: d })}
          tabs={[
            { id: 'privacy', label: 'Privacy', icon: <ShieldCheck size={18} /> },
            { id: 'terms', label: 'Terms', icon: <ScrollText size={18} /> },
            { id: 'parents', label: 'Parents', icon: <Users size={18} /> },
            { id: 'credits', label: 'Credits', icon: <Heart size={18} /> },
          ]}
        />
      }
    >
      <div class="legal-page" key={doc}>
        <h2>{TITLES[doc]}</h2>
        <LegalBody doc={doc} />
      </div>
    </Screen>
  );
}
